import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { filterbaar, isStandaard, sorteerbaar, type Context, type Datumbereik, type Filterwaarde, type Lijst } from '@xxtract/tabelfilters';
import { DatumFilter } from './DatumFilter';
import { KeuzelijstFilter } from './KeuzelijstFilter';
import { ResetKnop } from './ResetKnop';
import { RICHTING_KEYS } from './SorteerbareKop';
import { SorteerKeuze } from './SorteerKeuze';
import { TekstFilter } from './TekstFilter';
import type { TabelData } from './useTabelData';
import type { TabelStand } from './useTabelStand';
import { CONTROL_CLASS } from './stijl';

const FILTER_ICON = 'filter_list';

interface FilterbalkProps<R, C extends Context> {
  lijst: Lijst<R, C>;
  stand: TabelStand;
  data: TabelData;
  /** Moet stabiel zijn (`useMemo`); dezelfde context als voor `useTabelData`. */
  context: C;
  /** Paginafilters: een eigen rij die niet inklapt (CAP-13, spec-filterbalk-smal-scherm). */
  paginafilters?: ReactNode;
  /** De bron legt de volgorde op: geen sorteerkeuze op een smal scherm. */
  sorteringVergrendeld?: boolean;
  /** Reset zonder `title`-tooltip (werkkwaliteit verbiedt hover-only informatie). */
  resetZonderTooltip?: boolean;
}

const actiefFilter = (f: Filterwaarde | undefined) => f !== undefined && !isStandaard({ f }, []);

/**
 * Kolomfilters per veldtype, reset en één live-regio voor treffers en sorteerreeks (CAP-2..7, CAP-11).
 * Onder 768 px staan de kolomfilters achter een knop "Filters (n)", met daarin de sorteerkeuze.
 */
export function Filterbalk<R, C extends Context>({ lijst, stand, data, context, paginafilters, sorteringVergrendeld, resetZonderTooltip }: FilterbalkProps<R, C>) {
  const { t } = useTranslation();
  const blokId = useId();
  const [uitgeklapt, setUitgeklapt] = useState(false);

  const kolommen = lijst.kolommen.filter((k) => filterbaar(k) && (k.zichtbaar?.(context) ?? true));
  const aantalActief = kolommen.filter((k) => actiefFilter(stand.filters[k.sleutel])).length;
  const kanSorteren = !sorteringVergrendeld && lijst.kolommen.some(sorteerbaar);
  const kop = (sleutel: string) => {
    const k = lijst.kolommen.find((x) => x.sleutel === sleutel);
    return k ? t(k.kopSleutel) : sleutel;
  };
  const reeks = stand.sortering.map((s) => `${kop(s.sleutel)} ${t(RICHTING_KEYS[s.richting])}`).join(', ');

  return (
    <div className="flex flex-col gap-3">
      {paginafilters ? <div className="flex flex-wrap items-end gap-3">{paginafilters}</div> : null}

      <div className="flex flex-wrap items-end gap-3">
        <button
          aria-controls={blokId}
          aria-expanded={uitgeklapt}
          className={`${CONTROL_CLASS} inline-flex flex-1 items-center gap-2 md:hidden`}
          onClick={() => setUitgeklapt((u) => !u)}
          type="button"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[18px]">{FILTER_ICON}</span>
          {aantalActief > 0 ? t('tabel.filtersMetAantal', { count: aantalActief }) : t('tabel.filters')}
        </button>

        <div className={`${uitgeklapt ? 'flex' : 'hidden'} order-last w-full flex-col gap-3 md:order-none md:flex md:w-auto md:flex-1 md:flex-row md:flex-wrap md:items-end`} id={blokId}>
          {kolommen.map((k) => {
            const f = stand.filters[k.sleutel];
            const zet = (w: Filterwaarde | undefined) => stand.zetFilter(k.sleutel, w);
            if (k.type === 'keuzelijst') {
              return (
                <KeuzelijstFilter
                  afgekapt={data.afgekapt}
                  gekozen={Array.isArray(f) ? f : []}
                  keuzes={data.keuzes[k.sleutel] ?? []}
                  key={k.sleutel}
                  kolom={k}
                  onChange={zet}
                  zonderAantallen={data.zonderAantallen}
                />
              );
            }
            if (k.type === 'datum') {
              return <DatumFilter bereik={f && typeof f === 'object' && !Array.isArray(f) ? (f as Datumbereik) : undefined} key={k.sleutel} kolom={k} onChange={zet} />;
            }
            return (
              <TekstFilter
                key={k.sleutel}
                kolom={k}
                onChange={zet}
                onFocusWissel={(focus) => data.opTekstFocus?.(focus ? k.sleutel : null)}
                suggesties={(tekst) => data.suggesties(k.sleutel, tekst)}
                tekst={typeof f === 'string' ? f : ''}
                vertraging={data.vertraging}
              />
            );
          })}
          {kanSorteren ? (
            <div className="md:hidden">
              <SorteerKeuze context={context} lijst={lijst} onChange={stand.zetSortering} sortering={stand.sortering} />
            </div>
          ) : null}
        </div>

        <ResetKnop onReset={stand.reset} uitgeschakeld={stand.isStandaard} zonderTooltip={resetZonderTooltip} />
      </div>

      <div aria-live="polite" className="sr-only" data-testid="tabel-aankondiging">
        {t('tabel.treffers', { count: data.totaal })}
        {reeks ? ` ${t('tabel.sorteringAangekondigd', { reeks })}` : ''}
      </div>
    </div>
  );
}
